import { useCallback } from "react";
import { AxiosError } from "axios";
import { ValidateTokenRequest, ValidateTokenSuccess, ApiErrorResponse } from "../../../../types/generated/models";
import { useClientPortalNavigate } from "../../../../hooks/useClientPortalRouter";
import { ROUTES } from "../../../../routes";
import { useClientPortalConfigContext, useResetPasswordContext } from "../../../../context";
import { usePostMutation } from "../../../../hooks/useClientPortalQuery";

interface UseTokenValidation {
    validateToken: (token: string) => Promise<void>;
    isPending: boolean;
}

export function useTokenValidation(): UseTokenValidation {
    const { clientPortalGuid } = useClientPortalConfigContext();
    const { setShowResetPasswordDialog, setClientPublicId } = useResetPasswordContext();
    const navigateTo = useClientPortalNavigate();

    const { mutateAsync, isPending } = usePostMutation<
        ValidateTokenSuccess,
        AxiosError<ApiErrorResponse>,
        ValidateTokenRequest
    >({
        url: `/api/v1/${clientPortalGuid}/Auth/ValidateResetPasswordToken`,
    });

    const validateToken = useCallback(
        async (token: string): Promise<void> => {
            try {
                const response = await mutateAsync({ token } as ValidateTokenRequest);
                setClientPublicId(response?.clientPublicId);
                setShowResetPasswordDialog(true);
            } catch {
                /* We need to have catch because we are rethrowing error in useClientPortalQuery 
                Should be fixed in https://zanda.atlassian.net/browse/PD-14743 */
            } finally {
                navigateTo(ROUTES.HOME.urlPath);
            }
        },
        [mutateAsync, setClientPublicId, setShowResetPasswordDialog, navigateTo]
    );

    return { validateToken, isPending };
}
